// useTickets — the tickets hub's server state (TanStack Query over apiClient;
// ADR-010): the list (the two arms of visibility are the SERVER's — the query
// carries only the lenses), its count (the phone drawer's one question), the
// detail (the peek), the filing acts (close · batch close · reopen). Every
// mutation invalidates the tickets prefix so the list + the peek refetch
// together (the star lives in useFollows — one verb for every followable kind).
//
// The list and the detail are LIVE reads (`liveGet` — FRONTEND-PATTERNS §Live
// updates): while on screen they follow what arrives from the field — the
// list's HEAD only (ADR-075 §3): page 1, of the stream or of the plain list;
// a deeper page is read when asked and refreshed by the person's own act (a
// page walked to is reading, not watching). The count is not live — asked per
// draft while the drawer is open.

import { keepPreviousData, useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { CountResponse } from '@ems-app/schemas/common/pagination';
import type { TicketsCloseRequest, TicketsListQuery } from '@ems-app/schemas/tickets/requests';
import type { TicketDetail, TicketsCloseResponse, TicketsListResponse } from '@ems-app/schemas/tickets/responses';
import type { TicketGroupBy } from '@ems-app/schemas/tickets/vocabulary';
import { apiClient } from '../lib/apiClient.js';
import { liveGet } from '../lib/live/liveGet.js';
import { framed, type ListFrame } from '../lib/bands.js';
import { refreshOnSettled } from '../lib/settled.js';

/** The view-state the list query travels on — the wire's own query type
 *  (ADR-009: one schemas workspace types both sides; a lens the wire does not
 *  know cannot be typed here). Arrays are CSV on the wire. */
export type TicketsListParams = Omit<Partial<TicketsListQuery>, 'limit' | 'offset'> & { limit: number; offset: number };

export const ticketsKeys = {
  /** List key — also the invalidation PREFIX (covers detail). */
  all: (projectId: string) => ['tickets', projectId] as const,
  list: (projectId: string, params: TicketsListParams) => ['tickets', projectId, 'list', params] as const,
  detail: (projectId: string, ticketId: string) => ['tickets', projectId, ticketId] as const,
};

function toWireParams(p: TicketsListParams): Record<string, string | number> {
  const out: Record<string, string | number> = { limit: p.limit, offset: p.offset };
  if (p.q) out.q = p.q;
  // The Created period and the Closed window ride with the screen's zone (ADR-082 §5 · §6) — the server cuts the days in it.
  if (p.created) out.created = p.created;
  if (p.closed) out.closed = p.closed;
  if (p.tz) out.tz = p.tz;
  if (p.sort_by) out.sort_by = p.sort_by;
  if (p.sort_dir) out.sort_dir = p.sort_dir;
  if (p.group_by) out.group_by = p.group_by;
  const csv = (k: string, v?: string[]) => {
    if (v && v.length > 0) out[k] = v.join(',');
  };
  csv('stage', p.stage);
  csv('fact', p.fact);
  csv('location', p.location);
  csv('person', p.person);
  csv('class', p.class);
  // The grouped view's open set (`*` = every band); absent = folded.
  csv('open', p.open);
  return out;
}

/** The list's answer, stamped with the FRAME it was asked in (`lib/bands` — the page renders the answer on screen in
 *  the frame that produced it, never the URL's). */
export type TicketsListAnswer = TicketsListResponse & { frame: ListFrame<TicketGroupBy> };

export function useTicketsList(projectId: string, params: TicketsListParams, opts: { enabled?: boolean } = {}): UseQueryResult<TicketsListAnswer, Error> {
  return useQuery({
    queryKey: ticketsKeys.list(projectId, params),
    queryFn: (ctx) =>
      (params.offset === 0
        ? liveGet<TicketsListResponse>(ctx, `/projects/${projectId}/tickets`, { params: toWireParams(params) })
        : apiClient.get<TicketsListResponse>(`/projects/${projectId}/tickets`, { params: toWireParams(params), signal: ctx.signal }).then((r) => r.data)
      ).then(framed(params)),
    enabled: projectId.length > 0 && (opts.enabled ?? true),
    placeholderData: keepPreviousData,
  });
}

/** The filtered POPULATION's count alone on the same predicates (ADR-078 §6) —
 *  the phone filter drawer's "Show results (n)", asked per draft after a pause
 *  (the drawer owns the pause, one question in flight, a stale answer dropped).
 *  The band parameters never ride it: the count is Σ every band whatever is
 *  opened, and the zone then rides only with a period, as the schema asks. A
 *  background question: a `BUSY` answer raises no toast (`quietBusy`). */
export async function countTickets(projectId: string, params: Omit<TicketsListParams, 'limit' | 'offset'>, signal?: AbortSignal): Promise<CountResponse> {
  const { limit: _l, offset: _o, group_by: _g, open: _p, ...wire } = toWireParams({ ...params, limit: 1, offset: 0 });
  const r = await apiClient.get<CountResponse>(`/projects/${projectId}/tickets/count`, { params: wire, signal, quietBusy: true });
  return r.data; // `capped` says the total is a floor (a window past the history cap) — the drawer prints "n+"
}

export function useTicketDetail(projectId: string, ticketId: string | null): UseQueryResult<TicketDetail, Error> {
  return useQuery({
    queryKey: ticketsKeys.detail(projectId, ticketId ?? ''),
    queryFn: (ctx) => liveGet<TicketDetail>(ctx, `/projects/${projectId}/tickets/${ticketId}`),
    enabled: projectId.length > 0 && !!ticketId,
  });
}

// ── the filing acts (ticket.capture:manage) ──────────────────────────────────

export function useCloseTicket(projectId: string): UseMutationResult<TicketDetail, Error, { ticketId: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId }) => apiClient.post<TicketDetail>(`/projects/${projectId}/tickets/${ticketId}/close`).then((r) => r.data),
    onSettled: refreshOnSettled(() => qc.invalidateQueries({ queryKey: ticketsKeys.all(projectId) })),
  });
}

/** "Close completed · n" — the batch by the page's FILTERS (ADR-078 §4): the wire carries the list's filter subset,
 *  never the loaded rows' ids; the server selects, caps and names the remainder. */
export function useCloseTickets(projectId: string): UseMutationResult<TicketsCloseResponse, Error, { filters: TicketsCloseRequest }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ filters }) => apiClient.post<TicketsCloseResponse>(`/projects/${projectId}/tickets/close`, filters).then((r) => r.data),
    onSettled: refreshOnSettled(() => qc.invalidateQueries({ queryKey: ticketsKeys.all(projectId) })),
  });
}

export function useReopenTicket(projectId: string): UseMutationResult<TicketDetail, Error, { ticketId: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId }) => apiClient.post<TicketDetail>(`/projects/${projectId}/tickets/${ticketId}/reopen`).then((r) => r.data),
    onSettled: refreshOnSettled(() => qc.invalidateQueries({ queryKey: ticketsKeys.all(projectId) })),
  });
}
